'use client';

import { FC, MouseEventHandler, useEffect, useState } from 'react';
import { Button } from '@tuic/web-compositions/core/Button';
import { ButtonGroup } from '@tuic/web-compositions/core/ButtonGroup';
import { useClientTranslation } from '@lib/i18n/client';
import { getPhaserAttributesFromUrl, PhaserClientData, setPhaserAttributesToUrl } from '@modules/phaser';
import { Locale } from '@tuic/i18n';
import { Token } from '@lib/token';
import { Phase } from '@lib/trip';
import { GetSlashDebugButton } from './get-slash-debug-button';
import { GetSlashAuthenticateForm } from './get-slash-authenticate-form';
import { checkBookedTariffCookie, deleteTariffRelatedMockCookies, toggleIncludedVolume, checkIncludedVolumeCookie, toggleDeviceLimit, checkDeviceLimitCookie } from '@modules/phaser/actions/internet-card';
type Props = {
  data: PhaserClientData;
  token: Token;
  locale: Locale;
};
export const InternetCard: FC<Props> = ({
  data,
  token,
  locale
}) => {
  let {
    t
  } = useClientTranslation();
  let [pending, setPending] = useState(false);
  let [pendingReset, setPendingReset] = useState(false);
  let [pendingIncludedVolume, setPendingIncludedVolume] = useState(false);
  let [pendingDeviceLimit, setPendingDeviceLimit] = useState(false);
  const [hasTariffBooked, setHasTariffBooked] = useState<boolean>();
  const [hasIncludedVolume, setHasIncludedVolume] = useState<boolean>();
  const [hasDeviceLimit, setHasDeviceLimit] = useState<boolean>();
  const [isActive, setIsActive] = useState<boolean>();
  const handleTestModeToggle: MouseEventHandler = ev => {
    if (typeof window === 'undefined') {
      return;
    }
    ev.preventDefault();
    setPending(true);
    const attrs = getPhaserAttributesFromUrl(window.location.href);
    const mockInternetCard = !attrs.mockInternetCard;
    const url = setPhaserAttributesToUrl(window.location.href, {
      ...attrs,
      mockInternetCard,
      ...(mockInternetCard ? {
        mpi: undefined,
        cabinNumber: undefined,
        shipCode: undefined
      } : {})
    });
    window.location.href = url.toString();
  };
  const handleReset: MouseEventHandler = async () => {
    setPendingReset(true);
    await deleteTariffRelatedMockCookies();
    setPendingReset(false);
    setHasTariffBooked(undefined);
    setHasIncludedVolume(false);
  };
  const handleIncludedVolumeToggle: MouseEventHandler = async () => {
    setPendingIncludedVolume(true);
    await toggleIncludedVolume();
    setPendingIncludedVolume(false);
    setHasIncludedVolume(undefined);
  };
  const handleDeviceLimitToggle: MouseEventHandler = async () => {
    setPendingDeviceLimit(true);
    setHasDeviceLimit(await toggleDeviceLimit());
    setPendingDeviceLimit(false);
  };
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const isActive = getPhaserAttributesFromUrl(window.location.href).mockInternetCard;
    setIsActive(isActive ?? false);
  }, []);
  useEffect(() => {
    if (typeof hasTariffBooked !== 'undefined') {
      return;
    }
    checkBookedTariffCookie().then(value => setHasTariffBooked(value));
  }, [hasTariffBooked]);
  useEffect(() => {
    if (typeof hasIncludedVolume !== 'undefined') {
      return;
    }
    checkIncludedVolumeCookie().then(value => setHasIncludedVolume(value));
  }, [hasIncludedVolume]);
  useEffect(() => {
    if (typeof hasDeviceLimit !== 'undefined') {
      return;
    }
    checkDeviceLimitCookie().then(value => setHasDeviceLimit(value));
  }, [hasDeviceLimit]);
  return <div className="space-y-10" data-sentry-component="InternetCard" data-sentry-source-file="internet-card.tsx">
      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="headline-md">{t('Internet-Card')}</h3>
          <p>
            {t('In dieser Rubrik können Sie den Test-Modus der Internet-Card starten. Im Test-Modus greift die Card auf lokale Endpunkte zurück und simuliert so das Verhalten wie im Bordnetzwerk der Mein Schiff ® Flotte.')}
          </p>
        </div>

        <Button variant="primary" indicating={pending || typeof isActive === 'undefined'} onClick={handleTestModeToggle} data-sentry-element="Button" data-sentry-source-file="internet-card.tsx">
          {isActive ? t('Test-Modus ausschalten') : t('Test-Modus einschalten')}
        </Button>
      </div>

      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="headline-md">{t('Tarife')}</h3>
          <p>
            {t('Verwalten Sie die Test-Tarife für die Internet-Card. Sie können gebuchte Tarife entfernen oder Inklusiv-Volumen hinzufügen, um das Verhalten bei mehreren verfügbaren Tarifen zu testen.')}
          </p>
        </div>

        <ButtonGroup data-sentry-element="ButtonGroup" data-sentry-source-file="internet-card.tsx">
          <Button variant="secondary" type="button" indicating={typeof hasTariffBooked === 'undefined' || pendingReset} disabled={hasTariffBooked === false} onClick={handleReset} data-sentry-element="Button" data-sentry-source-file="internet-card.tsx">
            {t('Gebuchten Tarif entfernen')}
          </Button>
          <Button variant="secondary" type="button" indicating={typeof hasIncludedVolume === 'undefined' || pendingIncludedVolume} disabled={!isActive} onClick={handleIncludedVolumeToggle} data-sentry-element="Button" data-sentry-source-file="internet-card.tsx">
            {hasIncludedVolume ? t('Inklusiv-Volumen entfernen') : t('Inklusiv-Volumen hinzufügen')}
          </Button>
        </ButtonGroup>
      </div>

      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="headline-md">{t('Geräte-Limit')}</h3>
          <p>
            {t('Simulieren Sie das Erreichen des Geräte-Limits bei der Tarifbuchung. Wenn aktiviert, wird beim Versuch einen Tarif zu aktivieren eine Fehlermeldung angezeigt, die darauf hinweist, dass die maximale Anzahl der erlaubten Geräte bereits erreicht wurde.')}
          </p>
        </div>

        <Button variant="secondary" type="button" indicating={typeof hasDeviceLimit === 'undefined' || pendingDeviceLimit} disabled={!isActive} onClick={handleDeviceLimitToggle} data-sentry-element="Button" data-sentry-source-file="internet-card.tsx">
          {hasDeviceLimit ? t('Limit deaktivieren') : t('Limit aktivieren')}
        </Button>
      </div>

      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="headline-md">{t('Debug-Daten kopieren')}</h3>
          <p>
            {t('Mit dieser Funktion können Sie Debug-Daten von GetSlash-Endpunkten abrufen und in Ihre Zwischenablage kopieren.')}
          </p>
        </div>

        <GetSlashDebugButton credentials={data.getSlash.credentials} data-sentry-element="GetSlashDebugButton" data-sentry-source-file="internet-card.tsx" />
      </div>

      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="headline-md">{t('Mit Produktions-Daten anmelden')}</h3>
          <p>
            {t('Um die Funktionalität der Internet-Card in der Test-Umgebung im Bordnetzwerk des Schiffs prüfen zu können, müssen Sie sich mit einer Reise aus der Produktions-Umgebung anmelden.')}
          </p>
        </div>

        <GetSlashAuthenticateForm locale={locale} data-sentry-element="GetSlashAuthenticateForm" data-sentry-source-file="internet-card.tsx" />
      </div>
    </div>;
};